import { getPatientDetail } from '../../../services/ApiService'
import {
  PatientDetailsDto,
  PatientSummaryDto
} from '../../../types/swaggerTypes'
import {
  getCastorEConsentUrl,
  getCastorEdcUrl,
  getValidCastorEdcUrl,
  openCastorEdcUrl,
  openCastorEdcUrlWhenResolved,
  openExternalUrl,
  openExternalUrlWhenResolved
} from '../../../utils/rwdFollowUpEligibility'
import { PatientOrderStatus } from './Patient.model'

export const PTA_PROVIDE_CONSENT_ACTION_KEY =
  'patient_detail:patient_actions.provide_consent'

export const PTA_PROVIDE_BASELINE_DATA_ACTION_KEY =
  'patient_detail:patient_actions.provide_baseline_data'

export interface PatientDashboardActionItem {
  text: string
  onClick: (patient: PatientSummaryDto) => void | Promise<void>
}

type CastorUrlSource = PatientSummaryDto | PatientDetailsDto

type CastorUrlSelector = (source: CastorUrlSource) => string | null | undefined

type CastorUrlBuilder = (url?: string | null) => string

const selectEConsentUrl: CastorUrlSelector = (source) =>
  source.castorEConsentUrl

const selectEdcUrl: CastorUrlSelector = (source) => source.castorEdcUrl

/** Reads the Castor deep link from patient details, which the summary row omits. */
export const resolveCastorUrlFromPatientDetail = async (
  patient: PatientSummaryDto,
  selectUrl: CastorUrlSelector,
  buildUrl: CastorUrlBuilder
): Promise<string> => {
  if (!patient.patientId || !patient.physicianId) {
    return buildUrl(undefined)
  }

  const response = await getPatientDetail(
    patient.patientId.toString(),
    patient.physicianId
  )

  return buildUrl(selectUrl(response.data))
}

export const openCastorUrlForPatient = (
  patient: PatientSummaryDto,
  selectUrl: CastorUrlSelector,
  buildUrl: CastorUrlBuilder
): Promise<void> => {
  const summaryUrl = selectUrl(patient)

  if (getValidCastorEdcUrl(summaryUrl)) {
    openExternalUrl(buildUrl(summaryUrl))
    return Promise.resolve()
  }

  return openExternalUrlWhenResolved(
    () => resolveCastorUrlFromPatientDetail(patient, selectUrl, buildUrl),
    buildUrl(undefined)
  )
}

export const openCastorEdcUrlForPatient = (
  patient: PatientSummaryDto,
  userEmail?: string | null
): Promise<void> => {
  if (getValidCastorEdcUrl(patient.castorEdcUrl)) {
    openCastorEdcUrl(patient.castorEdcUrl, userEmail)
    return Promise.resolve()
  }

  return openCastorEdcUrlWhenResolved(
    () =>
      resolveCastorUrlFromPatientDetail(patient, selectEdcUrl, getCastorEdcUrl),
    getCastorEdcUrl(undefined),
    userEmail
  )
}

export const createProvideConsentAction = (
  onOpen?: (patient: PatientSummaryDto) => void
): PatientDashboardActionItem => ({
  text: PTA_PROVIDE_CONSENT_ACTION_KEY,
  onClick: (patient) => {
    onOpen?.(patient)
    return openCastorUrlForPatient(
      patient,
      selectEConsentUrl,
      getCastorEConsentUrl
    )
  }
})

export const provideConsentAction = createProvideConsentAction()

export const createProvideBaselineDataAction = (
  userEmail?: string | null,
  onOpen?: (patient: PatientSummaryDto) => void
): PatientDashboardActionItem => ({
  text: PTA_PROVIDE_BASELINE_DATA_ACTION_KEY,
  onClick: (patient) => {
    onOpen?.(patient)
    return openCastorEdcUrlForPatient(patient, userEmail)
  }
})

export const getPtaActionMenuItems = (
  patient: Pick<PatientSummaryDto, 'patientOrderStatus'>,
  userEmail?: string | null,
  onOpen?: (patient: PatientSummaryDto) => void
): PatientDashboardActionItem[] => {
  switch (patient.patientOrderStatus) {
    case PatientOrderStatus.AwaitingConsent:
      return [createProvideConsentAction(onOpen)]
    case PatientOrderStatus.AwaitingBaselineData:
      return [createProvideBaselineDataAction(userEmail, onOpen)]
    default:
      return []
  }
}
